import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { VersionService } from '../version.service';

import { default as MENU_DATA } from './menu.data';

export interface IMenuServiceData {
    class: string;
    items: IMenuItem[];
}

export interface IMenuServiceDot {
    [key: string]: boolean;
}

export interface IMenuServiceBadge {
    [key: string]: string;
}

export interface IMenuServiceAttention {
    [key: string]: string;
}

@Injectable()
export class MenuService {
    callback: any = null;

    _visible: boolean = false;
    _visibleSbj: Subject<boolean> = new Subject();
    visible$ = this._visibleSbj.asObservable();
    get isVisible(): boolean {
        return this._visible;
    }

    _dataSbj: BehaviorSubject<IMenuServiceData[]> = new BehaviorSubject(clone(MENU_DATA));
    data$: Observable<IMenuServiceData[]> = this._dataSbj.asObservable();
    get data() {
        return this._dataSbj.getValue();
    }

    _settingsSbj: BehaviorSubject<any> = new BehaviorSubject({});
    settings$: Observable<any> = this._settingsSbj.asObservable();

    _eventsSbj: Subject<IMenuItem> = new Subject();
    events$: Observable<IMenuItem> = this._eventsSbj.asObservable();

    _dotSbj: BehaviorSubject<IMenuServiceDot> = new BehaviorSubject({});
    dot$: Observable<IMenuServiceDot> = this._dotSbj.asObservable();

    _badgeSbj: BehaviorSubject<IMenuServiceBadge> = new BehaviorSubject({});
    badge$: Observable<IMenuServiceBadge> = this._badgeSbj.asObservable();

    _attentionSbj: BehaviorSubject<IMenuServiceAttention> = new BehaviorSubject({});
    attention$: Observable<IMenuServiceAttention> = this._attentionSbj.asObservable();

    constructor(
        private _router: Router,

        private _versionSrv: VersionService
    ) {
        this._versionSrv.data$
            .pipe(tap((versionData: IVersionData) => {
                this.extend(versionData);
            }))
            .subscribe();

        this.visible$
            .pipe(tap((isVisible) => this._visible = isVisible))
            .subscribe();

        this.visible$
            .pipe(tap((isVisible) => {
                if (isVisible) {
                    document.body.classList.add('menu--visible');
                } else {
                    document.body.classList.remove('menu--visible');
                }
            }))
            .subscribe();
    }

    emit(item: IMenuItem) {
        this._eventsSbj.next(item);
    }

    show(): void {
        this._visibleSbj.next(true);
    }

    hide(): void {
        this._visibleSbj.next(false);
    }

    dot(id: string, data: boolean) {
        const state = this._dotSbj.getValue();

        this._dotSbj.next({
            ...state,
            [id]: data
        });
    }

    badge(id: string, data: string) {
        const state = this._badgeSbj.getValue();

        this._badgeSbj.next({
            ...state,
            [id]: data
        });
    }

    get(id: string): IMenuItem {
        const menuData = this._dataSbj.getValue();

        let menuItem = null
        for (const menuSection of menuData) {
            menuItem = menuSection.items.find((menuItem) => menuItem.id === id);

            if (menuItem) {
                break;
            }
        }

        return menuItem; 
    }

    patch(id: string, data: any) {
        let menuData = this._dataSbj.getValue();

        menuData = menuData.map((menuSection) => {
            const menuItemIdx = menuSection.items.findIndex((menuItem) => menuItem.id === id);
            if (!~menuItemIdx) {
                return menuSection;
            }

            const menuItem = menuSection.items[menuItemIdx];
            menuSection.items.splice(menuItemIdx, 1, {
                ...menuItem,
                ...data
            });

            return {
                ...menuSection,
                items: [...menuSection.items]
            };        
        });

        this._dataSbj.next(menuData);
    }

    attention(id: string, data: string) {
        const state = this._attentionSbj.getValue();

        this._attentionSbj.next({
            ...state,
            [id]: data
        });
    }

    toggle(): void {
        this._visibleSbj.next(!this._visible);
    }

    extend(versionData: IVersionData): void {
        console.debug('MenuService::extend', versionData);

        const _menuData = clone(MENU_DATA);
        const _menuSettings = {};

        let config = [...this._router.config];

        if (versionData.serverData.features) {
            versionData.serverData.features.forEach(({
                modules = [],
                menuSettings = {},
                menuData = []
            }) => {
                Object.assign(_menuSettings, menuSettings);

                modules.forEach(async (module) => {
                    config = extendConfig(module, config);
                });

                menuData.forEach(async ({ item, section, start, after, deleteCount = 0, module }) => {
                    if (!isFinite(section) || section < 0 || section > _menuData.length) {
                        return;
                    }

                    const items = _menuData[section].items;

                    // Hack: place after Arena if Persistent world menu item detected
                    if (item.template && item.template.includes('Persistent world:')) {
                        after = 'Arena';
                    }

                    if (after) {
                        const idx = items.findIndex(({ label }) => label === after);
                        start = idx + 1;
                    }

                    if (item && isFinite(start) && isFinite(deleteCount)) {
                        items.splice(start, deleteCount, item);
                    }

                    if (!item && isFinite(start) && isFinite(deleteCount)) {
                        items.splice(start, deleteCount);
                    }

                    if (item.config) {
                        this._versionSrv.getData(item.config.API_URL).then((versionData) => {
                            item.versionData = versionData;
                        });
                    }

                    // TODO: Support old version
                    if (module) {
                        const _module = prepareModule(module);

                        config = extendConfig(_module, config);
                    }
                });
            });
        }

        this._router.resetConfig(config);
        this._settingsSbj.next(_menuSettings);
        this._dataSbj.next(_menuData);
    }

    reconfig() {
        this.callback && this.callback();
    }
}

function prepareModule(module) {
    return {
        name: module,
        module: module.substr(0, module.length - 6).toLowerCase(),
        path: module.substr(0, module.length - 6).toLowerCase()
    };
}

function clone(data): IMenuServiceData[] {
    return data.map((state) => ({
        ...state,
        items: [...state.items]
    }));
}

function extendConfig({ name, path, module }, config) {
    const idx = config.findIndex((route) => route.path === path);

    if (!!~idx) {
        config.splice(idx, 1);
    }

    return [...config, {
        path,
        loadChildren: () => import(`../${ module }.module`).then(m => m[name])
    }];
}
